/**
 * AuthorizeService — BFF edition
 *
 * Replaces the IS4/oidc-client OIDC flow with a simple cookie-session-backed
 * token fetch.  The server issues short-lived JWTs via GET /api/auth/token
 * (endpoint protected by the ASP.NET Core Identity application cookie).
 *
 * Flow:
 *   1. User logs in via the Razor Pages /Identity/Account/Login page.
 *   2. After login the SPA loads; the first call to getAccessToken() fetches
 *      a JWT from the server using the existing HttpOnly session cookie.
 *   3. React attaches the JWT as "Authorization: Bearer <token>" on all API
 *      calls (unchanged from the previous IS4-based flow).
 *   4. One minute before expiry the next getAccessToken() call silently
 *      re-fetches a fresh JWT (session cookie is still valid).
 *   5. If the session cookie has expired /api/auth/token returns 401
 *      and the user is redirected to the login page.
 */

const TOKEN_ENDPOINT = '/api/auth/token';
const RENEWAL_BUFFER_MS = 60_000; // renew 1 minute before expiry

export class AuthorizeService {
    _token = null;
    _tokenExpiresAt = null;
    _callbacks = [];
    _nextSubscriptionId = 0;
    _inflight = null; // deduplicates concurrent getAccessToken() calls

    async getAccessToken() {
        if (this._token && Date.now() < this._tokenExpiresAt - RENEWAL_BUFFER_MS) {
            return this._token;
        }

        // If a fetch is already in flight, wait for it instead of firing a second one.
        // This prevents N concurrent AuthorizeRoute mounts from each sending their own
        // request to /api/auth/token (which produces N 401s in the console on first load).
        if (this._inflight) {
            return this._inflight;
        }

        this._inflight = (async () => {
            try {
                const resp = await fetch(TOKEN_ENDPOINT, { credentials: 'include' });

                if (resp.status === 401 || resp.status === 403) {
                    return null;
                }

                if (!resp.ok) {
                    console.error('AuthorizeService: unexpected error from token endpoint', resp.status);
                    return null;
                }

                const contentType = resp.headers.get('content-type') ?? '';
                if (!contentType.includes('application/json')) {
                    console.warn('AuthorizeService: /api/auth/token returned non-JSON content-type; treating as unauthenticated.');
                    return null;
                }

                const { token, expiresIn } = await resp.json();
                const wasAuthenticated = !!this._token;
                this._token = token;
                this._tokenExpiresAt = Date.now() + expiresIn * 1000;
                if (!wasAuthenticated) {
                    this.notifySubscribers();
                }
                return this._token;
            } finally {
                this._inflight = null;
            }
        })();

        return this._inflight;
    }

    async isAuthenticated() {
        try {
            const t = await this.getAccessToken();
            return !!t;
        } catch {
            return false;
        }
    }

    async getUser() {
        const token = await this.getAccessToken();
        if (!token) return null;
        try {
            return JSON.parse(atob(token.split('.')[1]));
        } catch {
            return null;
        }
    }

    signOut() {
        this._token = null;
        this._tokenExpiresAt = null;
        window.location.href = '/Identity/Account/Logout';
    }

    subscribe(callback) {
        this._callbacks.push({ callback, subscription: this._nextSubscriptionId++ });
        return this._nextSubscriptionId - 1;
    }

    unsubscribe(subscriptionId) {
        this._callbacks = this._callbacks.filter(
            el => el.subscription !== subscriptionId
        );
    }

    notifySubscribers() {
        for (const { callback } of this._callbacks) {
            callback();
        }
    }

    error(message) { return { status: AuthenticationResultStatus.Fail, message }; }
    success(state) { return { status: AuthenticationResultStatus.Success, state }; }
    redirect()     { return { status: AuthenticationResultStatus.Redirect }; }

    static get instance() { return authService; }
}

const authService = new AuthorizeService();

export default authService;

export const AuthenticationResultStatus = {
    Redirect: 'redirect',
    Success:  'success',
    Fail:     'fail'
};
